import alloy from "@alloyidentity/web-sdk";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { useEvaluateConsumerDocumentsMutation } from "@lending/hooks/queries/useEvaluateConsumerDocumentsMutation";
import { useGetConsumerApplicationQuery } from "@lending/hooks/queries/useGetConsumerApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useStore } from "../../../../contexts/StoreContext";
import type { useLocalization } from "@fluent/react";
import type { ConsumerApplication } from "@lending/types";

interface ApplicantAddress {
  street_address?: string | null;
  street_address_2?: string | null;
  city?: string | null;
  region_code?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
}

interface AccountApplicationApplicant {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  address?: ApplicantAddress | null;
  is_primary?: boolean;
  kyc_id?: string | null;
}

interface AccountApplicationData {
  state: string;
  kyc_id?: string | null;
  applicants?: AccountApplicationApplicant[];
}

function isApplicantAddress(value: unknown): value is ApplicantAddress {
  if (!value || typeof value !== "object") {
    return false;
  }

  return true;
}

function mapApplicant(applicant: unknown): AccountApplicationApplicant | null {
  if (!applicant || typeof applicant !== "object") {
    return null;
  }

  const mappedApplicant = applicant as Record<string, unknown>;

  if (typeof mappedApplicant.id !== "string") {
    return null;
  }

  return {
    id: mappedApplicant.id,
    first_name:
      typeof mappedApplicant.first_name === "string" || mappedApplicant.first_name === null
        ? mappedApplicant.first_name
        : undefined,
    last_name:
      typeof mappedApplicant.last_name === "string" || mappedApplicant.last_name === null
        ? mappedApplicant.last_name
        : undefined,
    address: isApplicantAddress(mappedApplicant.address) ? mappedApplicant.address : null,
    is_primary:
      typeof mappedApplicant.is_primary === "boolean" ? mappedApplicant.is_primary : undefined,
    kyc_id:
      typeof mappedApplicant.kyc_id === "string" || mappedApplicant.kyc_id === null
        ? mappedApplicant.kyc_id
        : undefined,
  };
}

function mapConsumerApplication(application: ConsumerApplication): AccountApplicationData {
  const applicationData = application as ConsumerApplication & { kyc_id?: string | null };

  return {
    state: application.state,
    kyc_id: applicationData.kyc_id,
    applicants: application.applicants.map(mapApplicant).filter((applicant) => applicant !== null),
  };
}

interface InstitutionAlloyConfig {
  dao_alloy_doc_verification_sdk?: {
    documentation_sdk_public_key?: string | null;
    react_app_alloy_production?: boolean | string | null;
  } | null;
  dao_alloy_validation_prechecks?: boolean;
  ao_alloy_journey_token?: string | null;
}

interface AlloyCallbackParams {
  evaluation_token?: string;
  status: string;
  _embedded?: {
    entity_applications?: {
      entity_application_outcome: string | null;
    }[];
  };
}

interface UseMembershipShareDocumentWidgetProps {
  accountApplication: AccountApplicationData | null;
  primaryApplicant: AccountApplicationApplicant | null;
  accountApplicationUuid?: string;
  l10n: ReturnType<typeof useLocalization>["l10n"];
}

const DEFAULT_COLORS = {
  primary: "#4A61F2",
  secondary: "#2642EF",
};

const DEFAULT_THEME = {
  primaryColor: "#4A61F2",
  backgroundColor: "#FFFFFF",
  textColor: "#4C4C4C",
  successColor: "#37B374",
  errorColor: "#D93B3B",
};

function useMembershipShareDocumentsRequested() {
  const navigate = useNavigateWithSearch();
  const { data } = useStore();
  const aoApplicationQuery = useGetConsumerApplicationQuery(data.accountApplicationUuid);
  const [accountApplication, setAccountApplication] = useState<AccountApplicationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!data.accountApplicationUuid) {
      Sentry.captureMessage(
        "Missing accountApplicationUuid on membership share documents requested page",
        {
          level: "error",
        }
      );
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (aoApplicationQuery.isLoading) {
      setIsLoading(true);
      return;
    }

    if (aoApplicationQuery.isError || !aoApplicationQuery.data) {
      Sentry.captureException(aoApplicationQuery.error);
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (aoApplicationQuery.data.state !== "docs_requested") {
      void navigate("../status", { replace: true });
      return;
    }

    setAccountApplication(mapConsumerApplication(aoApplicationQuery.data));
    setHasError(false);
    setIsLoading(false);
  }, [
    aoApplicationQuery.data,
    aoApplicationQuery.error,
    aoApplicationQuery.isError,
    aoApplicationQuery.isLoading,
    data.accountApplicationUuid,
    navigate,
  ]);

  const primaryApplicant = useMemo(() => {
    return (
      accountApplication?.applicants?.find((applicant) => applicant.is_primary) ??
      accountApplication?.applicants?.[0] ??
      null
    );
  }, [accountApplication]);

  return {
    accountApplication: accountApplication as AccountApplicationData | null,
    primaryApplicant,
    isLoading,
    hasError,
    accountApplicationUuid: data.accountApplicationUuid,
  };
}

function useMembershipShareDocumentWidget({
  accountApplication,
  primaryApplicant,
  accountApplicationUuid,
  l10n,
}: UseMembershipShareDocumentWidgetProps) {
  const navigate = useNavigateWithSearch();
  const { institution } = useGetInstitutionQuery();
  const { showErrorToast } = useToastsContext();
  const evaluateDocumentsMutation = useEvaluateConsumerDocumentsMutation();
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const institutionAlloyConfig = institution as typeof institution & InstitutionAlloyConfig;

  useEffect(() => {
    return () => {
      alloy.close();
    };
  }, []);

  const handleWidgetError = (error?: unknown) => {
    if (error) {
      Sentry.captureException(error);
    }
    alloy.close();
    setIsSubmitting(false);
    setIsWidgetOpen(false);
    showErrorToast(
      l10n.getString(
        "membership-share-documents-requested-error",
        null,
        "There was an error opening the document upload flow. Please try again."
      )
    );
  };

  const submitDocumentsRequested = async () => {
    if (!accountApplicationUuid) {
      throw new Error("Missing accountApplicationUuid when submitting documents requested");
    }

    await evaluateDocumentsMutation.mutateAsync({
      params: { path: { uuid: accountApplicationUuid } },
    });
  };

  const openDocumentWidget = async () => {
    if (!accountApplication || !primaryApplicant?.kyc_id) {
      handleWidgetError(
        new Error("Missing account application data required to open Alloy widget")
      );
      return;
    }

    const applicantAddress = primaryApplicant.address ?? {};
    const alloySdkKey =
      institutionAlloyConfig.dao_alloy_doc_verification_sdk?.documentation_sdk_public_key ?? "";
    const isAlloyProduction =
      String(
        institutionAlloyConfig.dao_alloy_doc_verification_sdk?.react_app_alloy_production ?? ""
      ).toLowerCase() === "true";

    const baseConfig = {
      key: alloySdkKey,
      production: isAlloyProduction,
      maxEvaluationAttempts: 2,
      documents: ["license", "passport"],
      color:
        institution.primary_color && institution.secondary_color
          ? {
              primary: institution.primary_color,
              secondary: institution.secondary_color,
            }
          : DEFAULT_COLORS,
      validationPreChecks: institutionAlloyConfig.dao_alloy_validation_prechecks ?? false,
      customStyle: {
        theme: {
          ...DEFAULT_THEME,
          primaryColor: institution.primary_color_hex || DEFAULT_THEME.primaryColor,
        },
        componentOverride: {},
      },
    };

    const callback = async ({ evaluation_token, status, _embedded }: AlloyCallbackParams) => {
      const entityApplication = _embedded?.entity_applications?.[0];

      if (entityApplication?.entity_application_outcome === "approved") {
        alloy.close();
        setIsWidgetOpen(false);
        void navigate("../status", { replace: true });
        return;
      }

      if (["closed", "pending_step_up"].includes(status)) {
        alloy.close();
        setIsWidgetOpen(false);
        setIsSubmitting(false);
        return;
      }

      // "waiting_review" means the documents were submitted and the Alloy
      // journey application is in manual review — a success for this widget.
      const isDocsSubmitted =
        status === "waiting_review" || (Boolean(evaluation_token) && status === "complete");

      if (!isDocsSubmitted) {
        handleWidgetError(new Error(`Unexpected Alloy callback status: ${status}`));
        return;
      }

      try {
        setIsSubmitting(true);
        await submitDocumentsRequested();
        alloy.close();
        setIsWidgetOpen(false);
        void navigate("../submitting-documents", { replace: true });
      } catch (error) {
        handleWidgetError(error);
      }
    };

    const handleAlloyCallback = (params: AlloyCallbackParams) => {
      void callback(params);
    };

    try {
      setIsWidgetOpen(true);

      if (accountApplication.kyc_id) {
        await alloy.init({
          ...baseConfig,
          journeyToken: institutionAlloyConfig.ao_alloy_journey_token ?? undefined,
          journeyApplicationToken: accountApplication.kyc_id,
          isSingleEntity: true,
          entityToken: primaryApplicant.kyc_id,
        });
      } else {
        await alloy.init({
          ...baseConfig,
          entityToken: primaryApplicant.kyc_id,
          evaluationData: {
            nameFirst: primaryApplicant.first_name ?? undefined,
            nameLast: primaryApplicant.last_name ?? undefined,
            addressLine1: applicantAddress.street_address ?? undefined,
            addressLine2: applicantAddress.street_address_2 ?? undefined,
            addressCity: applicantAddress.city ?? undefined,
            addressState: applicantAddress.region_code ?? undefined,
            addressPostalCode: applicantAddress.postal_code ?? undefined,
            addressCountryCode: applicantAddress.country_code ?? undefined,
          },
        });
      }

      void alloy.open(handleAlloyCallback, "membership-share-documents-requested-container");
    } catch (error) {
      handleWidgetError(error);
    }
  };

  return {
    isWidgetOpen,
    isSubmitting,
    openDocumentWidget,
  };
}

export {
  useMembershipShareDocumentWidget,
  useMembershipShareDocumentsRequested,
  type AccountApplicationApplicant,
  type AccountApplicationData,
};
